/**
 * ImageUploadField — 运营图片直传控件（选文件 → 上传 → 缩略预览 → 清除）
 *
 * 表单里存的是**上传后的引用字符串**（landing 素材是 R2 key，其它域可能是相对 ref），
 * 本组件不认识它的语义，只负责「拿到 File → 交给 upload → 把返回的引用回吐给表单」。
 *
 * 预览两种来源：刚选的文件用本地 objectURL（免一次往返），已存在的引用由调用方给
 * `resolvePreview` 去换（landing 素材在私有桶里，得鉴权代理读）。两种 objectURL 都在
 * 替换与卸载时 revoke —— 不 revoke 的话运营每重选一次就漏一个 blob。
 */

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Icons } from '~/lib/icons';
import { toast } from '~/ui/Toast';

export function ImageUploadField({
  value,
  onChange,
  upload,
  resolvePreview,
  label,
  hint,
  width = 104,
  height = 104,
  disabled,
}: {
  /** 当前引用（空串 = 未上传）*/
  value: string;
  onChange: (next: string) => void;
  /** 真正的上传动作，返回要存进表单的引用 */
  upload: (file: File) => Promise<string>;
  /** 把已存在的引用换成可显示的 URL；不传 = 已有引用不预览 */
  resolvePreview?: (ref: string) => Promise<string>;
  label: string;
  hint?: ReactNode;
  width?: number;
  height?: number;
  disabled?: boolean;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  /** 本地 objectURL 需要 revoke；远端换来的 URL 也是 objectURL，同样要管 */
  const objectUrlRef = useRef<string | null>(null);

  /** useCallback 稳定引用：它进了下面 effect 的 deps，每 render 换新函数会让 effect 空转重跑 */
  const setObjectUrl = useCallback((next: string | null) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = next;
    setPreview(next);
  }, []);

  // 卸载时兜底 revoke
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  // 表单里已有引用（编辑既有任务）→ 换成可显示的 URL
  useEffect(() => {
    let cancelled = false;
    if (!value || !resolvePreview) return;
    // 刚上传完时 preview 已是本地 objectURL，不必再往返一次
    if (objectUrlRef.current) return;
    void resolvePreview(value)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setObjectUrl(url);
      })
      // 预览失败不该打断表单：引用还在、提交照常，只是看不到图
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [value, resolvePreview, setObjectUrl]);

  const pick = async (file: File) => {
    setPending(true);
    try {
      const ref = await upload(file);
      onChange(ref);
      setObjectUrl(URL.createObjectURL(file));
      toast(`${label}已上传`, { tone: 'pos', icon: 'IconCheck' });
    } catch (err) {
      toast(err instanceof Error ? err.message : `${label}上传失败`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
      <label
        className="vstack"
        style={{
          width,
          height,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          borderRadius: 'var(--radius)',
          border: `1px dashed var(--border${preview ? '' : '-strong'})`,
          background: preview ? 'var(--bg-inset)' : 'var(--bg-subtle)',
          backgroundImage: preview ? `url(${preview})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: preview ? '#fff' : 'var(--fg-muted)',
          textShadow: preview ? '0 1px 4px rgba(0,0,0,0.6)' : undefined,
          cursor: disabled || pending ? 'wait' : 'pointer',
        }}
      >
        <Icons.IconUpload size={16} />
        <span style={{ fontSize: 10.5 }}>{pending ? '上传中…' : preview ? '重新选择' : label}</span>
        <input
          type="file"
          accept="image/webp,image/png,image/jpeg"
          hidden
          disabled={disabled || pending}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void pick(f);
            // 清空以允许重选同一个文件
            e.target.value = '';
          }}
        />
      </label>
      <div className="vstack" style={{ gap: 4, minWidth: 0, paddingTop: 2 }}>
        {hint && (
          <span style={{ fontSize: 11, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{hint}</span>
        )}
        {value ? (
          <div className="hstack" style={{ gap: 6, alignItems: 'center', minWidth: 0 }}>
            <span
              className="mono"
              style={{
                fontSize: 10,
                color: 'var(--fg-faint)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={value}
            >
              {value}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={disabled || pending}
              onClick={() => {
                onChange('');
                setObjectUrl(null);
              }}
            >
              <Icons.IconX size={10} />
              移除
            </button>
          </div>
        ) : (
          <span style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>webp / png / jpeg · ≤2MB</span>
        )}
      </div>
    </div>
  );
}
